import classNames from 'classnames';
import { DateTime } from 'luxon';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../../../components/Button/Button';
import { ButtonVariants } from '../../../../../../../components/Button/Button.types';
import Modal from '../../../../../../../components/Modal/Modal';
import { showToast } from '../../../../../../../components/Toast/Toast';
import { ToastTypes } from '../../../../../../../components/Toast/Toast.types';
import {
  useDeleteMaintenanceFreeze,
  useDeleteNationalProgram,
} from '../../../../../../../services/bundles/bundles.service';
import { useDeleteGlobalFreeze } from '../../../../../../../services/freeze/freeze.service';
import { FreezeType } from '../../../../../../../types/freeze.types';
import useConfirmationModal from '../../../../../../../utils/useConfirmationModal/useConfirmationModal';
import styles from './DeleteConfirmationModal.module.css';
import { DeleteConfirmationModalProps } from './DeleteConfirmationModal.types';

const DeleteConfirmationModal: FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  freezeType,
  freezeName,
  startDate,
  endDate,
  idToDelete,
  openConfirmationModal,
}) => {
  const { t } = useTranslation();
  const { mutate: deleteNationalProgram, isLoading: deleteNationalProgramIsLoading } = useDeleteNationalProgram();
  const { mutate: deleteMaintenanceFreeze, isLoading: deleteMaintenanceFreezeIsLoading } = useDeleteMaintenanceFreeze();
  const { mutate: deleteGlobalFreeze } = useDeleteGlobalFreeze();

  const { setTitle, setHideStatusUpdateText } = useConfirmationModal();
  const handleDeleteSuccess = () => {
    onClose();
    setTitle(t('freezeCard.deleteSuccess', { freezeName }));
    setHideStatusUpdateText(freezeType === FreezeType.NATIONAL_PROGRAM || freezeType === FreezeType.MAINTENANCE);
    openConfirmationModal();
  };
  const handleDeleteError = (error: unknown) => {
    const { message } = error as { message: string };
    if (message !== 'API Gateway request timed out') {
      showToast({
        toastType: ToastTypes.Error,
        label: t('error.deletingThing', { thing: freezeType.split('_').join(' ').toLowerCase() }),
        id: 'freezeDeleteError',
      });
    }
  };

  const handleDelete = () => {
    switch (freezeType) {
      case FreezeType.MAINTENANCE:
        deleteMaintenanceFreeze(idToDelete, { onSuccess: handleDeleteSuccess, onError: handleDeleteError });
        break;
      case FreezeType.NATIONAL_PROGRAM:
        deleteNationalProgram(idToDelete, { onSuccess: handleDeleteSuccess, onError: handleDeleteError });
        break;
      case FreezeType.GLOBAL_FREEZE:
      default:
        deleteGlobalFreeze(idToDelete, { onSuccess: handleDeleteSuccess, onError: handleDeleteError });
        break;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      showTitleDivider
      headerContent={t('freezeCard.deleteConfirmTitle')}
      bodyContent={
        <div className={styles.modalBody}>
          <div className={styles.modalRow}>
            <span className={styles.modalLabel}>{t('freezeCard.freezeType')}:</span>
            <span className={classNames(styles.modalData, styles.capitalize)}>
              {t(`freezeCard.types.${freezeType}`)}
            </span>
          </div>
          <div className={styles.modalRow}>
            <span className={styles.modalLabel}>{t('common.startDate')}:</span>
            <span className={styles.modalData}>{DateTime.fromISO(startDate, { setZone: true }).toFormat('D')}</span>
          </div>
          <div className={styles.modalRow}>
            <span className={styles.modalLabel}>{t('common.endDate')}:</span>
            <span className={styles.modalData}>{DateTime.fromISO(endDate, { setZone: true }).toFormat('D')}</span>
          </div>
        </div>
      }
      footerContent={
        <div className={styles.modalButtons}>
          <Button variant={ButtonVariants.Secondary} onClick={onClose} type="button">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            type="button"
            isLoading={deleteNationalProgramIsLoading || deleteMaintenanceFreezeIsLoading}
          >
            {t('common.delete')}
          </Button>
        </div>
      }
    />
  );
};

export default DeleteConfirmationModal;
